import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import api from '@/services/apiSgft';
import { CompanyMappingResponse, EmployeeOption } from '../types';

export const fetchCompanyMapping = async (): Promise<CompanyMappingResponse> => {
  try {
    const response = await api.get('/company/mapping');
    const companies = response.data?.companies || response.data || [];
    return {
      companies: Array.isArray(companies) ? companies : [],
    };
  } catch (error) {
    toast.error('Erro ao carregar dados de empresas');
    throw error;
  }
};

export const fetchEmployees = async (managementId?: number): Promise<EmployeeOption[]> => {
  try {
    const response = await api.get('/employees/approval-flow/filter', {
      params: managementId ? { management: [managementId] } : {},
    });
    return response.data.map((employee: { id: number; name: string }) => ({
      value: employee.id,
      label: employee.name,
    }));
  } catch (error) {
    toast.error('Erro ao carregar colaboradores');
    return [];
  }
};

export const fetchAllEmployees = async (employeeName?: string): Promise<EmployeeOption[]> => {
  try {
    const response = await api.get('/employees/approval-flow/filter', {
      params: {
        ...(employeeName ? { search: employeeName } : {}),
      },
    });
    return response.data.map((employee: { id: number; name: string }) => ({
      value: employee.id,
      label: employee.name,
    }));
  } catch (error) {
    toast.error('Erro ao carregar colaboradores');
    return [];
  }
};

export const fetchEmployeeById = async (employeeId: number): Promise<EmployeeOption> => {
  try {
    const response = await api.get(`/employees/${employeeId}`);
    const employee = response.data[0];
    return {
      value: employee.id,
      label: employee.name,
    };
  } catch (error) {
    return {
      value: employeeId,
      label: `ID: ${employeeId}`,
    };
  }
};

export const useCompanyMapping = () => {
  return useQuery(
    ['company-mapping'],
    fetchCompanyMapping,
    {
      retry: false,
    }
  );
};

export const useEmployees = (managementId?: number) => {
  return useQuery(
    ['employees-filter', managementId],
    () => fetchEmployees(managementId),
    {
      enabled: !!managementId && managementId !== 0,
      retry: false,
    }
  );
};

export const useAllEmployees = (employeeName?: string) => {
  return useQuery(
    ['employees-all', employeeName],
    () => fetchAllEmployees(employeeName),
    {
      retry: false,
      staleTime: 30000,
    }
  );
};

export const useEmployeeById = (employeeId: number | undefined, enabled: boolean) => {
  return useQuery(
    ['employee-by-id', employeeId],
    () => fetchEmployeeById(employeeId!),
    {
      enabled: enabled && !!employeeId && employeeId > 0,
      retry: false,
      staleTime: Infinity,
    }
  );
};

